import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OfflineQueue = ({ isOnline, queuedScans, onSyncQueue, onClearQueue }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  useEffect(() => {
    if (isOnline && queuedScans?.length > 0) {
      // Auto-sync when coming back online
      handleSync();
    }
  }, [isOnline]);

  const handleSync = async () => {
    try {
      await onSyncQueue();
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Sync failed:', error);
    }
  };

  if (queuedScans?.length === 0 && isOnline) {
    return null;
  }

  return (
    <div className={`bg-card rounded-lg shadow-elevation-2 border-l-4 ${
      isOnline ? 'border-l-success' : 'border-l-warning'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isOnline ? 'bg-success bg-opacity-10' : 'bg-warning bg-opacity-10'
          }`}>
            <Icon 
              name={isOnline ? "Wifi" : "WifiOff"} 
              size={16} 
              className={isOnline ? "text-success" : "text-warning"} 
            />
          </div>
          <div>
            <h3 className="text-sm font-medium text-foreground">
              {isOnline ? 'Online' : 'Offline Mode'}
            </h3>
            <p className="text-xs text-muted-foreground">
              {queuedScans?.length} scan{queuedScans?.length !== 1 ? 's' : ''} queued
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {queuedScans?.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-muted-foreground hover:text-foreground"
            />
          )}
        </div>
      </div>
      {/* Connection Status */}
      <div className="px-4 pb-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            Status: {isOnline ? 'Connected' : 'Disconnected'}
          </span>
          {lastSyncTime && (
            <span className="text-muted-foreground">
              Last sync: {lastSyncTime?.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
      {/* Queued Scans */}
      {isExpanded && queuedScans?.length > 0 && (
        <div className="border-t border-border">
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Queued Scans</h4>
              <div className="flex space-x-2">
                {isOnline && (
                  <Button
                    variant="default"
                    size="sm"
                    iconName="RefreshCw"
                    onClick={handleSync}
                  >
                    Sync Now
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Trash2"
                  onClick={onClearQueue}
                  className="text-error hover:text-error"
                >
                  Clear
                </Button>
              </div>
            </div>

            <div className="space-y-2 max-h-48 overflow-y-auto">
              {queuedScans?.map((scan, index) => (
                <div key={scan?.id || index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      scan?.isValid ? 'bg-success bg-opacity-20' : 'bg-error bg-opacity-20'
                    }`}>
                      <Icon 
                        name={scan?.isValid ? "Check" : "X"} 
                        size={12} 
                        className={scan?.isValid ? "text-success" : "text-error"} 
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {scan?.data?.attendeeName || 'Unknown'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {scan?.timestamp?.toLocaleTimeString()} • {scan?.type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} className="text-warning" />
                    <span className="text-xs text-muted-foreground">Pending</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Offline Instructions */}
      {!isOnline && (
        <div className="border-t border-border p-4">
          <div className="bg-warning bg-opacity-10 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
              <div className="text-xs">
                <p className="font-medium text-foreground mb-1">Offline Mode Active</p>
                <p className="text-muted-foreground">
                  Scans are being stored locally and will sync automatically when connection is restored.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineQueue;